import { defineMessages } from 'react-intl';

/**
 * New strings for the navbar live-index redesign (Phase 1): the row-two index strip's figure
 * labels and freshness stamp, the search launcher's placeholder, and the guest value sheet.
 * Everything else (My Jobs, Language, Menu, Advanced Filters, Location, the four browse-by word
 * links) reuses its existing message id, see nav-live-index components.
 */
export const navIndexMessages = defineMessages({
  openJobs: {
    id: 'nav.index.openJobs',
    defaultMessage: 'open jobs',
    description: 'Navbar index strip: label after the open-jobs figure, e.g. "41,203 open jobs"',
  },
  newToday: {
    id: 'nav.index.newToday',
    defaultMessage: 'new today',
    description: 'Navbar index strip: label after the new-jobs-today figure, e.g. "1,104 new today"',
  },
  employersHiring: {
    id: 'nav.index.employersHiring',
    defaultMessage: 'employers hiring',
    description: 'Navbar index strip: label after the hiring-employers figure, e.g. "4,882 employers hiring"',
  },
  remote: {
    id: 'nav.index.remote',
    defaultMessage: 'remote',
    description: 'Navbar index strip: label after the remote-jobs figure, e.g. "3,120 remote"',
  },
  updatedAgo: {
    id: 'nav.index.updatedAgo',
    defaultMessage: 'Updated {minutes} min ago',
    description: 'Navbar index strip: freshness stamp, minutes since the figures were generated',
  },
  searchPlaceholder: {
    id: 'nav.search.placeholder',
    defaultMessage: 'Search jobs, skills or city',
    description: 'Navbar live-index search bar placeholder on non-search pages',
  },
  searchShortcutAria: {
    id: 'nav.search.shortcutAria',
    defaultMessage: 'Shortcut: / to focus search',
    description:
      'Accessibility label for the "/" keyboard-shortcut badge shown at the right edge of an empty search bar',
  },
  sheetFree: {
    id: 'nav.sheet.free',
    defaultMessage: 'Account',
    description: 'Guest value sheet: eyebrow for a first-time visitor with no local history',
  },
  sheetKeep: {
    id: 'nav.sheet.keep',
    defaultMessage: 'Keep what you have',
    description: 'Guest value sheet: eyebrow for a visitor who already has recent searches or a viewed job',
  },
  sheetTrack: {
    id: 'nav.sheet.track',
    defaultMessage: 'Track every application',
    description: 'Guest value sheet: first pitch row, bold lead',
  },
  sheetTrackNote: {
    id: 'nav.sheet.trackNote',
    defaultMessage: 'saved, applied, interviews: one board',
    description: 'Guest value sheet: first pitch row, muted note',
  },
  sheetAlerts: {
    id: 'nav.sheet.alerts',
    defaultMessage: 'New matches by email',
    description: 'Guest value sheet: second pitch row, bold lead',
  },
  sheetAlertsNote: {
    id: 'nav.sheet.alertsNote',
    defaultMessage: '<em>{n}</em> new today, only the ones that fit',
    description:
      'Guest value sheet: second pitch row, muted note with the live new-today figure. {n} is wrapped in an <em> tag ' +
      'the caller renders with its own styling (Phase 1 review fix F4); translate the surrounding words only, keep the <em>{n}</em> tag as-is.',
  },
  sheetAlertsNoteLoading: {
    id: 'nav.sheet.alertsNoteLoading',
    defaultMessage: 'new today, only the ones that fit',
    description:
      'Guest value sheet: second pitch row, muted note, shown without a number while the live new-today figure has not loaded yet',
  },
  sheetKit: {
    id: 'nav.sheet.kit',
    defaultMessage: 'Tailored CV and cover letter',
    description: 'Guest value sheet: third pitch row, bold lead',
  },
  sheetKitNote: {
    id: 'nav.sheet.kitNote',
    defaultMessage: 'one click per job',
    description: 'Guest value sheet: third pitch row, muted note',
  },
  sheetSearches: {
    id: 'nav.sheet.searches',
    defaultMessage: '{n} recent searches',
    description: 'Guest value sheet, "keep" variant: recent-searches row lead',
  },
  sheetViewed: {
    id: 'nav.sheet.viewed',
    defaultMessage: 'Last viewed',
    description: 'Guest value sheet, "keep" variant: last-viewed-job row lead',
  },
  sheetViewedCount: {
    id: 'nav.sheet.viewedCount',
    defaultMessage: '{n, plural, one {# viewed job} other {# viewed jobs}}',
    description:
      'Guest value sheet, "keep" variant, prerendered static header only: the viewedJobs cookie only carries job ' +
      'IDs (no titles) for an anonymous visitor, so the static twin (which has no client-side job-title lookup) ' +
      'shows a count instead of the hydrated header\'s "Last viewed: {title}" row. {n} is the count.',
  },
  sheetDevice: {
    id: 'nav.sheet.device',
    defaultMessage: 'On this device only. Sign in to keep them everywhere and get alerts.',
    description: 'Guest value sheet, "keep" variant: note explaining local-only storage',
  },
  sheetGoogle: {
    id: 'nav.sheet.google',
    defaultMessage: 'Continue with Google',
    description: 'Guest value sheet: primary sign-in button, "free" variant',
  },
  sheetKeepGoogle: {
    id: 'nav.sheet.keepGoogle',
    defaultMessage: 'Keep these, continue with Google',
    description: 'Guest value sheet: primary sign-in button, "keep" variant',
  },
  sheetEmail: {
    id: 'nav.sheet.email',
    defaultMessage: 'or use email',
    description: 'Guest value sheet: secondary sign-in link (email instead of Google)',
  },
  sheetFine: {
    id: 'nav.sheet.fine',
    defaultMessage: 'You can sign out any time.',
    description: 'Guest value sheet: fine print under the sign-in button',
  },
});
